import { buildApiUrl, createHeaders } from "./apiClient";

export const REVERSE_SEMINAR_CATEGORY = "Reverse Seminars";

export const REVERSE_SEMINAR_URGENCY_OPTIONS = ["low", "medium", "high", "critical"];
export const REVERSE_SEMINAR_DECISION_OPTIONS = ["pending", "accepted", "revise", "rejected"];
export const REVERSE_SEMINAR_STAGE_OPTIONS = [
  "challenge_brief",
  "research_response",
  "pilot_proposal",
  "decision_pending",
  "pilot_active",
  "pilot_completed",
  "archived",
];

export const REVERSE_SEMINAR_MILESTONE_STATUS_OPTIONS = [
  "pending",
  "in_progress",
  "completed",
  "blocked",
];

export const REVERSE_SEMINAR_PARTICIPANT_TYPES = [
  "industry",
  "academic",
  "moderator",
  "observer",
  "other",
];

export const REVERSE_SEMINAR_USE_CASES = [
  "Heat stress in vineyard operations",
  "Fermentation inconsistency",
  "Smoke exposure response",
  "Water-use optimization",
  "Disease management",
  "Labor bottlenecks",
  "Sustainability compliance",
  "Technology adoption",
];

export const REVERSE_SEMINAR_PILOT_TEMPLATES = [
  {
    id: "pilot_a_climate_resilience",
    title: "Pilot A: Vineyard Climate Resilience",
    duration_weeks: 12,
    scope: "One region, one theme (heat/water).",
    cohort: "3-5 wineries + 1-2 academic teams.",
    outputs: "Intervention playbook + measured outcomes.",
    kpis: "Yield stability, water-use efficiency, adoption intent.",
  },
  {
    id: "pilot_b_process_optimization",
    title: "Pilot B: Winery Process Optimization",
    duration_weeks: 10,
    scope: "Fermentation/process variability.",
    cohort: "2-4 wineries + microbiology/process experts.",
    outputs: "Validated protocol changes and risk controls.",
    kpis: "Defect reduction, batch variance reduction, operator usability.",
  },
  {
    id: "pilot_c_cross_region_transfer",
    title: "Pilot C: Cross-Region Knowledge Transfer",
    duration_weeks: 14,
    scope: "Same challenge across two regions.",
    cohort: "4-6 operators + extension/research network.",
    outputs: "Transferability matrix and region-adapted guidance.",
    kpis: "Replication success rate, time-to-adoption, cost-benefit ratio.",
  },
];

const STAGE_META = {
  challenge_brief: {
    label: "Challenge Brief",
    status: "open",
  },
  research_response: {
    label: "Research Response",
    status: "in-review",
  },
  pilot_proposal: {
    label: "Pilot Proposal",
    status: "assigned",
  },
  decision_pending: {
    label: "Decision Pending",
    status: "waiting",
  },
  pilot_active: {
    label: "Pilot Active",
    status: "active",
  },
  pilot_completed: {
    label: "Pilot Completed",
    status: "closed",
  },
  archived: {
    label: "Archived",
    status: "closed",
  },
};

const DECISION_META = {
  pending: { label: "Pending", colorScheme: "yellow" },
  accepted: { label: "Accepted", colorScheme: "green" },
  revise: { label: "Revise", colorScheme: "orange" },
  rejected: { label: "Rejected", colorScheme: "red" },
};

const normalizeValue = (value = "") =>
  String(value || "")
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "");

const parseJsonSafe = async (response) => {
  try {
    return await response.json();
  } catch (_) {
    return {};
  }
};

const throwIfNotOk = async (response, fallbackMessage) => {
  if (response.ok) return;
  const payload = await parseJsonSafe(response);
  throw new Error(payload?.error || fallbackMessage || `Request failed (${response.status})`);
};

const normalizeMilestones = (items) =>
  (Array.isArray(items) ? items : [])
    .map((item, idx) => {
      const title = String(item?.title || "").trim();
      if (!title) return null;
      const status = normalizeValue(item?.status || "pending");
      return {
        id: Number(item?.id) || undefined,
        title,
        due_at: item?.due_at || null,
        status: REVERSE_SEMINAR_MILESTONE_STATUS_OPTIONS.includes(status)
          ? status
          : "pending",
        notes: String(item?.notes || "").trim() || null,
        sort_order: Number(item?.sort_order) || (idx + 1) * 10,
        is_active: item?.is_active !== false,
      };
    })
    .filter(Boolean);

const normalizeParticipants = (items) =>
  (Array.isArray(items) ? items : [])
    .map((item, idx) => {
      const fullName = String(item?.full_name || "").trim();
      const userId = Number(item?.user_id);
      if (!fullName && !Number.isFinite(userId)) return null;
      const participantType = normalizeValue(item?.participant_type || "other");
      return {
        id: Number(item?.id) || undefined,
        user_id: Number.isFinite(userId) ? userId : null,
        full_name: fullName || null,
        role: String(item?.role || "").trim() || null,
        organization: String(item?.organization || "").trim() || null,
        participant_type: REVERSE_SEMINAR_PARTICIPANT_TYPES.includes(participantType)
          ? participantType
          : "other",
        created_by_user_id: Number(item?.created_by_user_id) || null,
        updated_by_user_id: Number(item?.updated_by_user_id) || null,
        created_at: item?.created_at || null,
        updated_at: item?.updated_at || null,
        sort_order: Number(item?.sort_order) || (idx + 1) * 10,
        is_active: item?.is_active !== false,
      };
    })
    .filter(Boolean);

const mapApiSeminar = (seminar = {}) => {
  const stage = normalizeValue(seminar?.stage || "challenge_brief") || "challenge_brief";
  const decision = normalizeValue(seminar?.decision || "pending") || "pending";
  const urgency = normalizeValue(seminar?.urgency || "medium") || "medium";

  return {
    id: Number(seminar?.id) || seminar?.id,
    scope: String(seminar?.scope || "theme").trim() || "theme",
    team_key: String(seminar?.team_key || "").trim(),
    category: REVERSE_SEMINAR_CATEGORY,
    title: String(seminar?.title || "").trim(),
    problem_statement: String(seminar?.problem_statement || "").trim(),
    urgency,
    constraints: String(seminar?.constraints || "").trim(),
    desired_outcome: String(seminar?.desired_outcome || "").trim(),
    research_response: String(seminar?.research_response || "").trim(),
    pilot_hypothesis: String(seminar?.pilot_hypothesis || "").trim(),
    pilot_design: String(seminar?.pilot_design || "").trim(),
    pilot_sites: String(seminar?.pilot_sites || "").trim(),
    pilot_timeline: String(seminar?.pilot_timeline || "").trim(),
    pilot_metrics: String(seminar?.pilot_metrics || "").trim(),
    pilot_owner: String(seminar?.pilot_owner || "").trim(),
    next_milestone: String(seminar?.next_milestone || "").trim(),
    use_case: String(seminar?.use_case || "").trim(),
    resources_needed: String(seminar?.resources_needed || "").trim(),
    decision,
    stage,
    pilot_template_id: String(seminar?.pilot_template_id || "").trim(),
    milestones: normalizeMilestones(seminar?.milestones),
    participants: normalizeParticipants(seminar?.participants),
    created_by_user_id: Number(seminar?.created_by_user_id) || null,
    updated_by_user_id: Number(seminar?.updated_by_user_id) || null,
    author_label: String(seminar?.author_label || seminar?.author_name || "").trim(),
    author_name: String(seminar?.author_name || seminar?.author_label || "").trim(),
    created_at: seminar?.created_at || null,
    updated_at: seminar?.updated_at || null,
    event_at: seminar?.event_at || seminar?.created_at || null,
    deleted_at: seminar?.deleted_at || null,
    sort_order: Number(seminar?.sort_order) || 100,
    is_active: seminar?.is_active !== false,
    raw: seminar,
  };
};

const buildSeminarPayload = ({
  scope,
  teamKey,
  seminar,
  currentUserId = null,
  isAdmin = false,
} = {}) => {
  const payload = {
    scope,
    team_key: teamKey,
    title: String(seminar?.title || "").trim(),
    problem_statement: String(seminar?.problem_statement || "").trim(),
    urgency: normalizeValue(seminar?.urgency || "medium") || "medium",
    constraints: String(seminar?.constraints || "").trim() || null,
    desired_outcome: String(seminar?.desired_outcome || "").trim(),
    research_response: String(seminar?.research_response || "").trim() || null,
    pilot_hypothesis: String(seminar?.pilot_hypothesis || "").trim() || null,
    pilot_design: String(seminar?.pilot_design || "").trim() || null,
    pilot_sites: String(seminar?.pilot_sites || "").trim() || null,
    pilot_timeline: String(seminar?.pilot_timeline || "").trim() || null,
    pilot_metrics: String(seminar?.pilot_metrics || "").trim() || null,
    pilot_owner: String(seminar?.pilot_owner || "").trim() || null,
    next_milestone: String(seminar?.next_milestone || "").trim() || null,
    use_case: String(seminar?.use_case || "").trim() || null,
    resources_needed: String(seminar?.resources_needed || "").trim() || null,
    decision: normalizeValue(seminar?.decision || "pending") || "pending",
    stage: normalizeValue(seminar?.stage || "challenge_brief") || "challenge_brief",
    pilot_template_id: String(seminar?.pilot_template_id || "").trim() || null,
    author_label: String(seminar?.author_label || "").trim() || null,
    event_at: seminar?.event_at || new Date().toISOString(),
    sort_order: Number(seminar?.sort_order) || 100,
    is_active: isAdmin ? seminar?.is_active !== false : true,
    created_by_user_id: Number(currentUserId) || null,
  };

  if (Array.isArray(seminar?.milestones)) {
    payload.milestones = normalizeMilestones(seminar.milestones);
    payload.replace_milestones = true;
  }

  if (Array.isArray(seminar?.participants)) {
    payload.participants = normalizeParticipants(seminar.participants);
    payload.replace_participants = true;
  }

  return payload;
};

export const getReverseSeminarStageMeta = (stage = "") => {
  const key = normalizeValue(stage || "challenge_brief");
  return STAGE_META[key] || STAGE_META.challenge_brief;
};

export const getReverseSeminarDecisionMeta = (decision = "") => {
  const key = normalizeValue(decision || "pending");
  return DECISION_META[key] || DECISION_META.pending;
};

export const buildReverseSeminarDraft = (overrides = {}) => ({
  title: "",
  problem_statement: "",
  urgency: "medium",
  constraints: "",
  desired_outcome: "",
  research_response: "",
  pilot_hypothesis: "",
  pilot_design: "",
  pilot_sites: "",
  pilot_timeline: "",
  pilot_metrics: "",
  pilot_owner: "",
  next_milestone: "",
  use_case: "",
  resources_needed: "",
  decision: "pending",
  stage: "challenge_brief",
  pilot_template_id: "",
  milestones: [],
  participants: [],
  author_label: "",
  sort_order: 100,
  is_active: true,
  ...overrides,
});

export const listReverseSeminars = async ({
  scope,
  teamKey,
  stage,
  decision,
  includeInactive = false,
  includeInactiveChildren = false,
  query = "",
  limit = 300,
  offset = 0,
  onlyMine = false,
} = {}) => {
  const params = new URLSearchParams();
  if (scope) params.set("scope", String(scope));
  if (teamKey) params.set("team_key", String(teamKey));
  if (stage) params.set("stage", String(stage));
  if (decision) params.set("decision", String(decision));
  if (includeInactive) params.set("include_inactive", "true");
  if (includeInactiveChildren) params.set("include_inactive_children", "true");
  if (onlyMine) params.set("only_mine", "true");
  if (query) params.set("q", String(query));
  params.set("limit", String(limit));
  params.set("offset", String(offset));

  const response = await fetch(buildApiUrl(`/reverse-seminars?${params.toString()}`), {
    method: "GET",
    headers: createHeaders({ json: false }),
  });
  await throwIfNotOk(response, "Failed to load reverse seminars");
  const payload = await parseJsonSafe(response);
  const items = Array.isArray(payload?.items) ? payload.items.map(mapApiSeminar) : [];
  return {
    ...payload,
    items,
  };
};

export const getReverseSeminar = async (seminarId, { includeInactiveChildren = false } = {}) => {
  const suffix = includeInactiveChildren ? "?include_inactive_children=true" : "";
  const response = await fetch(buildApiUrl(`/reverse-seminars/${seminarId}${suffix}`), {
    method: "GET",
    headers: createHeaders({ json: false }),
  });
  await throwIfNotOk(response, "Failed to load reverse seminar");
  const payload = await parseJsonSafe(response);
  return {
    ...payload,
    seminar: payload?.seminar ? mapApiSeminar(payload.seminar) : null,
  };
};

export const createReverseSeminar = async ({
  scope,
  teamKey,
  seminar,
  currentUserId = null,
  isAdmin = false,
} = {}) => {
  const payload = buildSeminarPayload({
    scope,
    teamKey,
    seminar,
    currentUserId,
    isAdmin,
  });

  const response = await fetch(buildApiUrl("/reverse-seminars"), {
    method: "POST",
    headers: createHeaders(),
    body: JSON.stringify(payload),
  });
  await throwIfNotOk(response, "Failed to create reverse seminar");
  const parsed = await parseJsonSafe(response);
  return {
    ...parsed,
    seminar: parsed?.seminar ? mapApiSeminar(parsed.seminar) : null,
  };
};

export const updateReverseSeminar = async (
  seminarId,
  {
    scope,
    teamKey,
    seminar,
    currentUserId = null,
    isAdmin = false,
  } = {},
) => {
  const payload = buildSeminarPayload({
    scope,
    teamKey,
    seminar,
    currentUserId,
    isAdmin,
  });

  const response = await fetch(buildApiUrl(`/reverse-seminars/${seminarId}`), {
    method: "PATCH",
    headers: createHeaders(),
    body: JSON.stringify(payload),
  });
  await throwIfNotOk(response, "Failed to update reverse seminar");
  const parsed = await parseJsonSafe(response);
  return {
    ...parsed,
    seminar: parsed?.seminar ? mapApiSeminar(parsed.seminar) : null,
  };
};

/**
 * Minimal decision PATCH for leader inbox — sends only `decision`
 * (and optionally `stage`) to the seminar endpoint without the full
 * `buildSeminarPayload` shape. Used by the inline Accept / Revise /
 * Reject buttons on reverse_seminar_case rows in the Leader Inbox.
 */
export const setReverseSeminarDecision = async (
  seminarId,
  { decision, stage = null } = {},
) => {
  const body = { decision };
  if (stage) body.stage = stage;
  const response = await fetch(buildApiUrl(`/reverse-seminars/${seminarId}`), {
    method: "PATCH",
    headers: createHeaders(),
    body: JSON.stringify(body),
  });
  await throwIfNotOk(response, "Failed to update reverse seminar decision");
  const parsed = await parseJsonSafe(response);
  return {
    ...parsed,
    seminar: parsed?.seminar ? mapApiSeminar(parsed.seminar) : null,
  };
};

export const deleteReverseSeminar = async (seminarId, { hard = false } = {}) => {
  const suffix = hard ? "?hard=true" : "";
  const response = await fetch(buildApiUrl(`/reverse-seminars/${seminarId}${suffix}`), {
    method: "DELETE",
    headers: createHeaders({ json: false }),
  });
  await throwIfNotOk(response, "Failed to delete reverse seminar");
  return parseJsonSafe(response);
};

export const createReverseSeminarMilestone = async (seminarId, payload = {}) => {
  const response = await fetch(buildApiUrl(`/reverse-seminars/${seminarId}/milestones`), {
    method: "POST",
    headers: createHeaders(),
    body: JSON.stringify(payload),
  });
  await throwIfNotOk(response, "Failed to create milestone");
  return parseJsonSafe(response);
};

export const updateReverseSeminarMilestone = async (seminarId, milestoneId, payload = {}) => {
  const response = await fetch(
    buildApiUrl(`/reverse-seminars/${seminarId}/milestones/${milestoneId}`),
    {
      method: "PATCH",
      headers: createHeaders(),
      body: JSON.stringify(payload),
    },
  );
  await throwIfNotOk(response, "Failed to update milestone");
  return parseJsonSafe(response);
};

export const deleteReverseSeminarMilestone = async (
  seminarId,
  milestoneId,
  { hard = false } = {},
) => {
  const suffix = hard ? "?hard=true" : "";
  const response = await fetch(
    buildApiUrl(`/reverse-seminars/${seminarId}/milestones/${milestoneId}${suffix}`),
    {
      method: "DELETE",
      headers: createHeaders({ json: false }),
    },
  );
  await throwIfNotOk(response, "Failed to delete milestone");
  return parseJsonSafe(response);
};

export const createReverseSeminarParticipant = async (seminarId, payload = {}) => {
  const response = await fetch(buildApiUrl(`/reverse-seminars/${seminarId}/participants`), {
    method: "POST",
    headers: createHeaders(),
    body: JSON.stringify(payload),
  });
  await throwIfNotOk(response, "Failed to create participant");
  return parseJsonSafe(response);
};

export const updateReverseSeminarParticipant = async (
  seminarId,
  participantId,
  payload = {},
) => {
  const response = await fetch(
    buildApiUrl(`/reverse-seminars/${seminarId}/participants/${participantId}`),
    {
      method: "PATCH",
      headers: createHeaders(),
      body: JSON.stringify(payload),
    },
  );
  await throwIfNotOk(response, "Failed to update participant");
  return parseJsonSafe(response);
};

export const deleteReverseSeminarParticipant = async (
  seminarId,
  participantId,
  { hard = false } = {},
) => {
  const suffix = hard ? "?hard=true" : "";
  const response = await fetch(
    buildApiUrl(`/reverse-seminars/${seminarId}/participants/${participantId}${suffix}`),
    {
      method: "DELETE",
      headers: createHeaders({ json: false }),
    },
  );
  await throwIfNotOk(response, "Failed to delete participant");
  return parseJsonSafe(response);
};
